import { Theme, ThemeInput, ColorScheme } from '../types'

const BASE_URL = '/api/v1/themes'

// --- CSRF ---

function csrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute('content') ?? ''
}

// --- Transform helpers ---

function camelColorScheme(raw: Record<string, string>): ColorScheme {
  return {
    primary: raw.primary,
    secondary: raw.secondary,
    accent: raw.accent,
    background: raw.background,
    surface: raw.surface,
    text: raw.text,
    textMuted: raw.text_muted,
  }
}

function snakeColorScheme(scheme: ColorScheme): Record<string, string> {
  return {
    primary: scheme.primary,
    secondary: scheme.secondary,
    accent: scheme.accent,
    background: scheme.background,
    surface: scheme.surface,
    text: scheme.text,
    text_muted: scheme.textMuted,
  }
}

// API response → frontend Theme
export function apiThemeToFrontend(raw: Record<string, any>): Theme {
  return {
    id: String(raw.id),
    slug: raw.slug,
    name: raw.name,
    description: raw.description ?? undefined,
    displayFont: raw.display_font?.family ?? 'Inter',
    displayWeight: raw.display_font?.weight ?? 'bold',
    bodyFont: raw.body_font?.family ?? 'Inter',
    bodyWeight: raw.body_font?.weight ?? 'normal',
    lightMode: camelColorScheme(raw.light_mode ?? {}),
    darkMode: camelColorScheme(raw.dark_mode ?? {}),
    borderRadius: raw.border_radius ?? 'md',
    shadow: raw.shadow ?? 'md',
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    systemTheme: raw.system_theme,
    userId: raw.user_id,
  }
}

// Frontend updates → API request body
export function frontendToApiPayload(
  updates: Partial<ThemeInput>
): Record<string, any> {
  const payload: Record<string, any> = {}

  if (updates.name !== undefined) payload.name = updates.name
  if (updates.description !== undefined) payload.description = updates.description
  if (updates.borderRadius !== undefined) payload.border_radius = updates.borderRadius
  if (updates.shadow !== undefined) payload.shadow = updates.shadow

  // Font fields: only send if either part changed
  if (updates.displayFont !== undefined || updates.displayWeight !== undefined) {
    payload.display_font = {
      family: updates.displayFont,
      weight: updates.displayWeight,
      letter_spacing: 'tracking-normal',
    }
  }
  if (updates.bodyFont !== undefined || updates.bodyWeight !== undefined) {
    payload.body_font = {
      family: updates.bodyFont,
      weight: updates.bodyWeight,
      letter_spacing: 'tracking-normal',
    }
  }

  if (updates.lightMode !== undefined) payload.light_mode = snakeColorScheme(updates.lightMode)
  if (updates.darkMode !== undefined) payload.dark_mode = snakeColorScheme(updates.darkMode)

  return payload
}

// --- API methods ---

async function request<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken(),
      ...((options.headers as Record<string, string>) ?? {}),
    },
    ...options,
  })

  if (res.status === 204) return undefined as unknown as T

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body.error || body.errors || `API error ${res.status}`)
  }

  return res.json()
}

export const themesApi = {
  list(): Promise<Theme[]> {
    return request<Record<string, any>[]>(`${BASE_URL}.json`).then((data) =>
      data.map(apiThemeToFrontend)
    )
  },

  create(input: ThemeInput): Promise<Theme> {
    const payload = frontendToApiPayload(input)
    return request<Record<string, any>>(`${BASE_URL}.json`, {
      method: 'POST',
      body: JSON.stringify({ theme: payload }),
    }).then(apiThemeToFrontend)
  },

  update(slug: string, updates: Partial<ThemeInput>): Promise<Theme> {
    const payload = frontendToApiPayload(updates)
    return request<Record<string, any>>(`${BASE_URL}/${slug}.json`, {
      method: 'PATCH',
      body: JSON.stringify({ theme: payload }),
    }).then(apiThemeToFrontend)
  },

  destroy(slug: string): Promise<void> {
    return request<void>(`${BASE_URL}/${slug}.json`, {
      method: 'DELETE',
    })
  },

  duplicate(slug: string): Promise<Theme> {
    return request<Record<string, any>>(`${BASE_URL}/${slug}/duplicate.json`, {
      method: 'POST',
    }).then(apiThemeToFrontend)
  },
}
